import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/use-translation';
import type { HomeState } from './hooks/use-home-state';

/**
 * The top of the home page: who the instrument on screen belongs to, and the
 * one thing left to do with it.
 *
 * On a first visit the demonstration piano is already loaded, so its button
 * opens it rather than loading it again. Once someone has their own profile,
 * the main button carries them on from wherever they stopped, and the demo
 * steps back to a link — loading it would overwrite their piano.
 */
export const HomeHero = t0 => {
  const $ = _c(42);
  const {
    home,
    onCreateProfile,
    onLoadDemo
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("Explore the demonstration piano");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("View the adjustment sheet");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("Set my target");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("Resume the measurements");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      demo: t1,
      designed: t2,
      measured: t3,
      measuring: t4
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const resumeLabel = t5[home.stage];
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h1 className="m-0 font-serif text-[clamp(58px,8.6vw,128px)] leading-[0.95] font-medium tracking-[-0.01em]">Piano Touch <span className="ptl-accent">Lab</span></h1>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("A toolkit for grand piano action balancing");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <p className="m-0 max-w-[30ch] text-[clamp(20px,2.2vw,27px)] leading-[1.35] font-serif italic text-muted-foreground text-balance">{t7}</p>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowRight className="size-4" />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== home.resumeTo || $[20] !== resumeLabel) {
    t10 = <Button asChild={true} size="lg"><Link to={home.resumeTo}>{resumeLabel}{t9}</Link></Button>;
    $[19] = home.resumeTo;
    $[20] = resumeLabel;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Plus className="size-4" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== home.stage || $[24] !== t) {
    t12 = home.stage === "demo" ? t("Create a piano profile") : t("New Profile", {
      ns: "navigation"
    });
    $[23] = home.stage;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== onCreateProfile || $[27] !== t12) {
    t13 = <Button variant="outlineStrong" size="lg" onClick={onCreateProfile}>{t11}{t12}</Button>;
    $[26] = onCreateProfile;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <div className="flex flex-wrap justify-center gap-3">{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== home.stage || $[33] !== onLoadDemo || $[34] !== t) {
    t15 = home.stage === "demo" ? <span className="font-mono text-xs text-ptl-ink-3">{t("Free \xB7 no account \xB7 your data stays in your browser")}</span> : <span className="text-sm text-ptl-ink-3">{t("Want to see the example again?")}{" "}<button type="button" onClick={onLoadDemo} className="ptl-link font-semibold underline">{t("Load the demonstration piano\u2026")}</button></span>;
    $[32] = home.stage;
    $[33] = onLoadDemo;
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = <div className="flex flex-col items-center gap-4 pt-2.5">{t14}{t15}</div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16 || $[40] !== t8) {
    t17 = <div className="flex flex-col items-center gap-6 text-center">{t6}{t8}{t16}</div>;
    $[39] = t16;
    $[40] = t8;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
};